// Dates, counts and product colours as the admin boards write them, always in UK time. Built on
// src/lib/format.js, which spells month and day names out itself.
import { uk, calendarDay, time, dayMonth, shortDate, longDate } from '../../lib/format.js'

const MONTHS3 = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const isDay = s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
// a timestamp, or a calendar date ('2026-10-21') read as that day
const at = t => (isDay(t) ? calendarDay(t) : uk(t))

export { time, dayMonth, shortDate, longDate }

// 1 person, 2 people; 1 recording, 3 recordings
export function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`
}

// 30 Sep 2026
export function ukDate(t) {
  if (!t) return ''
  const u = at(t)
  return `${u.d} ${MONTHS3[u.m - 1]} ${u.y}`
}
// Wednesday 30 September 2026
export function ukDateLong(t) {
  if (!t) return ''
  const u = at(t)
  return `${DAYS[u.dow]} ${u.d} ${MONTHS[u.m - 1]} ${u.y}`
}
// 30 Sep 2026, 7pm
export function ukDateTime(t) {
  if (!t) return ''
  return `${ukDate(t)}, ${time(t)}`
}
// 30 Sep, or 30 Sep 2025 when it isn't this year
export function ukDateShort(t, nowMs = Date.now()) {
  if (!t) return ''
  const u = at(t)
  return u.y === uk(nowMs).y ? `${u.d} ${MONTHS3[u.m - 1]}` : `${u.d} ${MONTHS3[u.m - 1]} ${u.y}`
}

// Today, Yesterday, 4 days ago, or the date once it's more than 60 days back; Never for nothing
export function whenAgo(t, nowMs = Date.now()) {
  if (!t) return 'Never'
  const days = uk(nowMs).day - at(t).day
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days <= 60) return `${days} days ago`
  return ukDateShort(t, nowMs)
}

// Today in London as YYYY-MM-DD, for a date field's value and its earliest day
export function todayInUk(nowMs = Date.now()) {
  const u = uk(nowMs)
  return `${u.y}-${String(u.m).padStart(2, '0')}-${String(u.d).padStart(2, '0')}`
}

// A timestamp's London day as YYYY-MM-DD
export function ukDayOf(t) {
  if (!t) return ''
  if (isDay(t)) return t
  const u = uk(t)
  return `${u.y}-${String(u.m).padStart(2, '0')}-${String(u.d).padStart(2, '0')}`
}

// A London date and time as an ISO timestamp: ukToIso('2026-10-01', '19:00') is 18:00 UTC
export function ukToIso(day, hhmm = '00:00') {
  if (!isDay(day) || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null
  const [y, m, d] = day.split('-').map(Number)
  const [hh, mm] = hhmm.split(':').map(Number)
  const guess = Date.UTC(y, m - 1, d, hh, mm)
  const u = uk(guess)
  const shown = Date.UTC(u.y, u.m - 1, u.d, u.hh, u.mm)
  return new Date(guess - (shown - guess)).toISOString()
}

// The end of a London day, for an end date picked in a date field: access then runs to the end of it
export function ukEndOfDay(day) {
  const start = ukToIso(day, '00:00')
  if (!start) return null
  const next = ukToIso(addDays(day, 1), '00:00')
  return new Date(Date.parse(next) - 1000).toISOString()
}

export function addDays(day, n) {
  const [y, m, d] = day.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + n))
  return t.toISOString().slice(0, 10)
}

// The dot beside a product's name: its own accent (tokens.css), by product
const DOT_BY_SLUG = {
  'funding-engine': 'var(--accent-fe)', accelerator: 'var(--accent-acc)', poyt: 'var(--accent-poyt)',
  cashflow: 'var(--accent-pcfl)', masterclass: 'var(--accent-masterclass)', mentorship: 'var(--accent-master)',
}
const DOT_BY_LOOK = {
  funding_engine: 'var(--accent-fe)', accelerator: 'var(--accent-acc)', poyt: 'var(--accent-poyt)', master: 'var(--accent-master)',
}
export function productDot(product) {
  if (!product) return 'var(--shell-ink-20)'
  return DOT_BY_SLUG[product.slug || product.product_slug] || DOT_BY_LOOK[product.look || product.product_look] || 'var(--shell-ink-20)'
}

// The boards' status pill for an access status or People's status_key
export function statusTone(status) {
  switch (status) {
    case 'suspended': case 'overdue': case 'secured': return 'attention'
    // a free account only (a replay's register page): the quiet look a pause has
    case 'paused': case 'free': return 'paused'
    case 'ended': case 'revoked': case 'refunded': case 'none': case 'expired': return 'ended'
    default: return 'active'
  }
}

// A Stripe product's name as the hub shows or stores it: Stripe's dashes, spaced hyphens included,
// written as commas (Adam's writing rules: no dashes), at most 120 characters
export function labelFromStripeName(name) {
  return String(name || '').replace(/\s+[\u2013\u2014-]+\s+/g, ', ').replace(/[\u2013\u2014]/g, ', ').replace(/\s+/g, ' ').trim().slice(0, 120)
}
